import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GridBuilderService } from './grid-builder.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-batch-action-edit',
    templateUrl: './batch-action-edit.component.html',
    styleUrls: ['./batch-action-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BatchActionEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  title = 'Add Batch Action';
  loading = false;
  saving = false;
  activeTabIndex = 0;

  tabHoverGrid = false;
  tabHoverScreen = false;

  callBackList: any[] = [];
  callBackDropdownItems: any[] = [];
  selectedCallBackItem: any = null;
  callBackLoading = true;

  gridActionData = {
    grid_action_key: '',
    actionName: '',
    icon: '',
    rawDataReq: '',
    callBackName: null as any,
  };

  gridScreenData = {
    screen_modal_key: '',
    modal_name: '',
    controller_path: '',
    modal_size: '',
    modal_css_path: '',
    is_locked: 'Y',
    template_path: '',
    modal_desc: '',
  };

  gridKey: string | number = '';
  clientKey: string | number = '';
  private actionData: any = null;

  constructor(
    @Optional() public dialogRef: MatDialogRef<BatchActionEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private gridBuilderService: GridBuilderService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.gridKey = this.data?.grid_key ?? '';
    this.clientKey = this.data?.client_key ?? '';
    this.actionData = this.data?.actionData ?? null;

    if (this.actionData?.grid_action_key) {
      this.title = 'Edit Batch Action';
      this.gridActionData.grid_action_key = this.actionData.grid_action_key;
      this.gridActionData.actionName = this.actionData.name ?? '';
      this.gridActionData.icon = this.actionData.icon ?? '';
      this.gridActionData.rawDataReq = this.actionData.row_data_required ?? '';
    }

    this.loadCallBackNames();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCallBackNames(): void {
    this.callBackLoading = true;
    this.gridBuilderService.getScreenModalName({ client_key: this.clientKey })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.callBackLoading = false;
          if (res?.callSuccess === '1' && res?.screenModalName) {
            this.callBackList = res.screenModalName;
            this.callBackDropdownItems = mapToAppkitDropdownItems(this.callBackList, 'modal_name', 'screen_modal_key');
          }

          if (this.actionData?.call_back) {
            const match = this.callBackList.find(
              (cb: any) => cb.modal_name === this.actionData.call_back
            );
            if (match) {
              this.gridActionData.callBackName = match;
              this.selectedCallBackItem = this.callBackDropdownItems.find(
                (item) => item.value === match.screen_modal_key || item.label === match.modal_name
              ) || null;
              this.gridScreenData.modal_name = match.modal_name ?? '';
              this.gridScreenData.controller_path = match.modal_controler ?? '';
              this.gridScreenData.modal_size = match.modal_size ?? '';
              this.gridScreenData.template_path = match.modal_template ?? '';
              this.gridScreenData.modal_css_path = match.modal_css_path ?? '';
              this.gridScreenData.modal_desc = match.modal_desc ?? '';
              this.gridScreenData.screen_modal_key = match.screen_modal_key ?? '';
            }
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.callBackLoading = false;
          this.snackbar.openSnackBar('Error loading callback names', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onCallBackChange(): void {
    const cb = this.gridActionData.callBackName;
    if (cb) {
      this.gridScreenData.modal_name = cb.modal_name ?? '';
      this.gridScreenData.controller_path = cb.modal_controler ?? '';
      this.gridScreenData.modal_size = cb.modal_size ?? '';
      this.gridScreenData.template_path = cb.modal_template ?? '';
      this.gridScreenData.modal_css_path = cb.modal_css_path ?? '';
      this.gridScreenData.modal_desc = cb.modal_desc ?? '';
      this.gridScreenData.screen_modal_key = cb.screen_modal_key ?? '';
    }
  }

  onCallBackDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedCallBackItem = selected;
    this.gridActionData.callBackName = selected || null;
    this.onCallBackChange();
  }

  onCallBackSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedCallBackItem = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  save(): void {
    if (this.activeTabIndex === 0) {
      this.gridActionSave();
    } else {
      this.screenModalSave();
    }
  }

  private gridActionSave(): void {
    if (!this.gridActionData.actionName?.trim()) {
      this.snackbar.openSnackBar('Action Name is required', 'gtw-snackbar--yellow');
      return;
    }
    this.saving = true;
    this.cdr.detectChanges();

    const payload = {
      grid_action_key: this.gridActionData.grid_action_key || undefined,
      name: this.gridActionData.actionName,
      icon: this.gridActionData.icon,
      row_data_required: this.gridActionData.rawDataReq,
      call_back: this.gridActionData.callBackName?.modal_name ?? '',
      grid_key: this.gridKey,
      client_key: this.clientKey,
    };

    this.gridBuilderService.saveGridAction(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.saving = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Grid Action saved successfully', 'gtw-snackbar--green');
            this.dialogRef.close('saved');
          } else {
            this.snackbar.openSnackBar('Error saving Grid Action', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.saving = false;
          this.snackbar.openSnackBar('Error saving Grid Action', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private screenModalSave(): void {
    this.saving = true;
    this.cdr.detectChanges();

    const payload = {
      screen_modal_key: this.gridScreenData.screen_modal_key || undefined,
      grid_action_key: this.gridActionData.grid_action_key || undefined,
      modal_name: this.gridScreenData.modal_name,
      modal_size: this.gridScreenData.modal_size,
      modal_template: this.gridScreenData.template_path,
      modal_controler: this.gridScreenData.controller_path,
      modal_class: this.gridScreenData.modal_css_path,
      modal_desc: this.gridScreenData.modal_desc,
      client_key: this.clientKey,
      lock_required: this.gridScreenData.is_locked,
    };

    this.gridBuilderService.saveScreenModal(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.saving = false;
          if (res?.callSuccess === '1') {
            this.snackbar.openSnackBar('Screen Modal saved successfully', 'gtw-snackbar--green');
            this.dialogRef.close('saved');
          } else {
            this.snackbar.openSnackBar('Error saving Screen Modal', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.saving = false;
          this.snackbar.openSnackBar('Error saving Screen Modal', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  compareCallBack(a: any, b: any): boolean {
    return a && b && a.modal_name === b.modal_name;
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
